import { useEffect, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { movies, categories } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [filteredMovies, setFilteredMovies] = useState<Movie[]>([]);
  const [currentCategory, setCurrentCategory] = useState(categories[0]);
  const [sortBy, setSortBy] = useState<'rating' | 'year' | 'update'>('update');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  useEffect(() => {
    const cat = categories.find(c => c.slug === slug) || categories[0];
    setCurrentCategory(cat);
    setCurrentPage(1);

    let result = slug === 'all' || !slug ? [...movies] : movies.filter(m => m.categorySlug === slug);

    switch (sortBy) {
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'year':
        result.sort((a, b) => b.year - a.year);
        break;
      case 'update':
      default:
        result.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
        break;
    }

    setFilteredMovies(result);
  }, [slug, sortBy]);

  const totalPages = Math.ceil(filteredMovies.length / pageSize);
  const paginatedMovies = filteredMovies.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: currentCategory.name },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <div className="flex items-center gap-3 mb-2">
            <div className="w-1 h-5 rounded-full bg-accent-500" />
            <h1 className="text-xl md:text-2xl font-heading text-foreground-950">
              {currentCategory.name}
            </h1>
            {currentCategory.slug !== 'all' && (
              <span className="text-sm text-foreground-500">({filteredMovies.length} 部)</span>
            )}
          </div>

          <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
            {categories.map(cat => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer flex-shrink-0 ${
                  slug === cat.slug
                    ? 'bg-accent-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800 border border-background-200/60'
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${cat.icon} text-sm`}></i>
                </span>
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center justify-between mb-5">
            <p className="text-sm text-foreground-500">
              {currentCategory.slug === 'all' ? '全部电影' : `${currentCategory.name}分类`} · 共 {filteredMovies.length} 部
            </p>
            <div className="flex items-center gap-2">
              {(['update', 'rating', 'year'] as const).map(opt => (
                <button
                  key={opt}
                  onClick={() => setSortBy(opt)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    sortBy === opt
                      ? 'bg-accent-500/15 text-accent-500'
                      : 'bg-background-100 text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {opt === 'update' ? '最近更新' : opt === 'rating' ? '评分最高' : '最新上映'}
                </button>
              ))}
            </div>
          </div>

          {paginatedMovies.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {paginatedMovies.map(movie => (
                  <div key={movie.id} className="flex justify-center">
                    <MovieCard movie={movie} size="normal" />
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10 pb-10">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-9 h-9 rounded-lg flex items-center justify-center bg-background-100 text-foreground-500 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        currentPage === page
                          ? 'bg-accent-500 text-background-50'
                          : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="w-9 h-9 rounded-lg flex items-center justify-center bg-background-100 text-foreground-500 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mb-4">
                <i className="ri-movie-2-line text-4xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无电影资源</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}